import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch, useLanguage } from "hooks";
import { useSelector } from "react-redux";
import { STORAGE_URL } from "constants";
import moment from "moment";
import {
  Layout,
  BookCard,
  ScrollList,
  UserReviewItem,
  ReviewForm,
  RatingStars,
  Skeletons,
} from "components";
import BookDetails from "./components/BookDetails";
import MagazineDetails from "./components/MagazineDetails";
import { Col, Container, Row } from "react-bootstrap";
import CurriculumDetails from "./components/CurriculumDetails";

/*
Each book has its own section each section with a different UI design that's why book_section_id return different component or different props for the same component
*/

export default function Details() {
  const language = useLanguage(); //language returned from useLanguage hook; it’s an object with current language key --> "value in current language";
  const navigate = useNavigate();
  const { book_id } = useParams();
  const [bookDetailsResponse, bookDetailsRequest] = useFetch(`getBookDetails`);
  const bookData = bookDetailsResponse.result.data;
  const { user } = useSelector((state) => state.auth); // call data from auth store
  const [totalRate, setTotalRate] = React.useState(0);
  const [reviews, setReviews] = React.useState([]);

  // without recall the data of book adding new review to the stat after sending it successfully
  const addReviewToList = React.useCallback(
    (data) => {
      setTotalRate(data.newAvgRating);
      setReviews([
        ...reviews,
        {
          user: {
            name: user.name,
            image: user.image,
          },
          comment: data.comment,
          rate: data.rate,
        },
      ]);
    },
    [reviews, user.image, user.name]
  );

  // to call book details API on change book_id from url
  React.useEffect(() => {
    if (book_id && typeof parseInt(book_id) === "number") {
      bookDetailsRequest(null, `?book_id=${book_id}`);
    }
    setTotalRate(0);
  }, [bookDetailsRequest, book_id]);

  React.useEffect(() => {
    if (bookDetailsResponse.result.status) {
      setTotalRate(bookData.details.rate);
      setReviews(bookData.details.reviews);
    }
  }, [bookDetailsResponse.result, bookData, book_id]);

  const setLink = (item) => {
    if (item.target === 0) navigate(`/show_link/${book_id}/${item.id}`);
    else window.open(item.link, "_blank");
  };

  return (
    <Layout key={book_id}>
      <div id="details_page" className="page_container pt-5">
        {/* book */}
        {bookData && bookData.details.book_section_id === 1 && (
          <BookDetails
            bookData={bookData} // bookData from bookDetailsResponse result
            setLink={setLink} // if user select a link it gonna go to show_link url to show the link data (video, sound, document, etc)
            details={(mainCategory) => [
              {
                label: language.printNumber,
                value: bookData.details.edition_no,
              },
              {
                label: language.place,
                value: bookData.details.publisher?.place,
              },
              {
                label: language.publisher,
                value: bookData.details.publisher?.name,
              },
              {
                label: language.date,
                value: moment(bookData.details?.publishing_date).format("YYYY"),
              },
              {
                label: language.category,
                value: mainCategory,
              },
              {
                label: language.pageNum,
                value: bookData.details.pages_count,
              },
            ]} // function take mainCategory should be a text with the main category that count in the component and return a list of objects label & value to show in the page
            images={bookData.details.images
              ?.map((item) => item.image)
              .map((item) => `${STORAGE_URL}${item}`)} // call images link from bookData details to make it links to the root image in website public storage
            user={user}
            schools={bookData?.details?.schools} // the schools the book available in
            links={bookData?.details?.links} // the book links
            book_id={book_id}
            mainDetails={[
              {
                label: language.theAuthor,
                value: bookData.details.author?.name,
              },
            ]} // a list of objects label & value to show in the page
          />
        )}

        {/* Magazine */}
        {bookData && bookData.details.book_section_id === 2 && (
          <MagazineDetails
            bookData={bookData}
            setLink={setLink}
            book_id={book_id}
          /> // component specialized in section 2 (magazine) show because its style different
        )}

        {/* Research And Studies */}
        {bookData && bookData.details.book_section_id === 3 && (
          <BookDetails
            bookData={bookData}
            setLink={setLink}
            details={(mainCategory) => [
              {
                label: language.dateR,
                value: moment(bookData.details?.publishing_date).format("YYYY"),
              },
              {
                label: language.breastSide,
                value: bookData.details.publisher?.name,
              },
              {
                label: language.pageNum,
                value: bookData.details.pages_count,
              },
              {
                label: language.category,
                value: mainCategory,
              },
              {
                label: language.place,
                value: bookData.details.publisher?.place,
              },
              {
                label: language.researchClassifying,
                value: bookData.details.bookclassifying?.name,
              },
            ]}
            images={[
              bookData.details.cover_image,
              ...bookData.details.images?.map((item) => item.image),
            ].map((item) => `${STORAGE_URL}${item}`)}
            user={user}
            links={bookData?.details?.links}
            book_id={book_id}
            mainDetails={[
              {
                label: language.researcherName,
                value: bookData.details.author?.name,
              },
            ]}
          />
        )}

        {/* Multimedia */}
        {bookData && bookData.details.book_section_id === 4 && (
          <BookDetails
            bookData={bookData}
            setLink={setLink}
            details={(mainCategory) => [
              {
                label: language.category,
                value: mainCategory,
              },
              {
                label: language.preparation,
                value: bookData.details.preparation,
              },
            ]}
            images={bookData.details.images
              ?.map((item) => item.image)
              .map((item) => `${STORAGE_URL}${item}`)}
            user={user}
            links={bookData?.details?.links}
            book_id={book_id}
            mainDetails={[]}
          />
        )}

        {/* Curriculum */}
        {bookData && bookData.details.book_section_id === 6 && (
          <CurriculumDetails
            bookData={bookData}
            setLink={setLink}
            images={[
              bookData.details.cover_image,
              ...bookData.details.images?.map((item) => item.image),
            ].map((item) => `${STORAGE_URL}${item}`)}
            user={user}
            schools={bookData?.details?.schools}
            links={bookData?.details?.links}
            book_id={book_id}
          /> // component specialized in section 2 (Curriculum) show because its style different
        )}

        {bookData && bookData.details.book_section_id !== 6 && (
          <React.Fragment>
            <Container>
              <hr className="bg-light4" style={{ marginBottom: 24 }} />
            </Container>

            {/* Reviews */}
            <section id="reviews" className="mb-3">
              <Container>
                <Row>
                  <Col xl={5} lg={6} md={12} sm={12} xs={12}>
                    <Row className="align-items-center mb-4 ">
                      <Col xl={5} lg={5} md={3} sm={4} xs={5}>
                        <h2 className="fw-medium text-primary">
                          {
                            language[
                              `bookRate${bookData.details.book_section_id}`
                            ]
                          }
                        </h2>
                      </Col>
                      <Col xl={7} lg={7} md={9} sm={8} xs={7}>
                        <Row className="align-items-center">
                          <Col xl={5} lg={6} md={3} sm={5} xs={8}>
                            <Row>
                              <Col xs={12}>
                                <RatingStars rate={totalRate} />
                              </Col>
                            </Row>
                            <Row className="mt-1">
                              <Col xs={12}>
                                <p className="text-light4 fw-medium">
                                  {language.rateRange}
                                </p>
                              </Col>
                            </Row>
                          </Col>
                          <Col xl={7} lg={6} md={9} sm={7} xs={4}>
                            <h1 className="fw-bold text-light5">
                              {totalRate ? totalRate.toFixed(1) : 0.0}
                            </h1>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {reviews.length > 0 && (
                  <Row>
                    <Col xl={8} lg={8} md={12} sm={12} xs={12}>
                      {reviews.map((item, index) => (
                        <UserReviewItem
                          key={index}
                          name={item.user.name}
                          image={item.user.image}
                          comment={item.comment}
                          rate={item.rate}
                          renderDivider={index < reviews.length - 1}
                        />
                      ))}
                    </Col>
                    <Col xl={4} lg={4} md={12} sm={12} xs={12}></Col>
                  </Row>
                )}

                {/* Leave Review */}
                <ReviewForm onSubmitted={addReviewToList} />
              </Container>
            </section>

            <Container>
              <hr className="bg-light4" style={{ marginBottom: 24 }} />
            </Container>

            <section id="suggestions" className="mb-3">
              <Container>
                {/* Suggestions */}
                <Row>
                  <Col xs={12}>
                    <Row>
                      <Col xs={12}>
                        <h2 className="fw-bold text-primary mb-4 ">
                          {
                            language[
                              `suggestedBooks${
                                bookData && bookData.details.book_section_id
                              }`
                            ]
                          }
                        </h2>
                      </Col>
                      <Col xs={12}>
                        <div className="slider_area mb-4 ">
                          {bookData && bookData.related.length > 0 ? (
                            <ScrollList
                              xs={{
                                slidesPerView: 1,
                                pagination: { clickable: true },
                              }}
                              sm={{
                                slidesPerView: 2,
                                pagination: { clickable: true },
                              }}
                              lg={{
                                slidesPerView: 3,
                              }}
                              xl={{
                                slidesPerView: 4,
                              }}
                              itemAs={BookCard}
                              items={bookData.related}
                              autoplay
                              loop
                            />
                          ) : (
                            <Skeletons.CardsSkeleton count={4} perRow={4} />
                          )}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={12}></Col>
                    </Row>
                  </Col>
                </Row>
              </Container>
            </section>
          </React.Fragment>
        )}
      </div>
    </Layout>
  );
}
